import React, { useState, useEffect, useMemo } from 'react';
import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import useText from '@/hooks/useText';

function Testimony({ variant = 'carousel' }: { variant?: 'carousel' | 'cards' }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const ariaPrev = useText('home.testimonials.aria_prev');
  const ariaNext = useText('home.testimonials.aria_next');
  const articleLink = useText('home.testimonials.article_link');
  const item1Text = useText('home.testimonials.item1_text');
  const item1Name = useText('home.testimonials.item1_name');
  const item1Company = useText('home.testimonials.item1_company');
  const item1Article = useText('home.testimonials.item1_article');
  const item2Text = useText('home.testimonials.item2_text');
  const item2Name = useText('home.testimonials.item2_name');
  const item2Company = useText('home.testimonials.item2_company');
  const item2Article = useText('home.testimonials.item2_article');
  const item3Text = useText('home.testimonials.item3_text');
  const item3Name = useText('home.testimonials.item3_name');
  const item3Company = useText('home.testimonials.item3_company');
  const item3Article = useText('home.testimonials.item3_article');
  const item4Text = useText('home.testimonials.item4_text');
  const item4Name = useText('home.testimonials.item4_name');
  const item4Company = useText('home.testimonials.item4_company');
  const item4Article = useText('home.testimonials.item4_article');
  const item5Text = useText('home.testimonials.item5_text');
  const item5Name = useText('home.testimonials.item5_name');
  const item5Company = useText('home.testimonials.item5_company');
  const item5Article = useText('home.testimonials.item5_article');

  const testimonials = useMemo(
    () => [
      { text: item1Text, name: item1Name, company: item1Company, article: item1Article?.trim() || undefined },
      { text: item2Text, name: item2Name, company: item2Company, article: item2Article?.trim() || undefined },
      { text: item3Text, name: item3Name, company: item3Company, article: item3Article?.trim() || undefined },
      { text: item4Text, name: item4Name, company: item4Company, article: item4Article?.trim() || undefined },
      { text: item5Text, name: item5Name, company: item5Company, article: item5Article?.trim() || undefined },
    ],
    [
      item1Text,
      item1Name,
      item1Company,
      item1Article,
      item2Text,
      item2Name,
      item2Company,
      item2Article,
      item3Text,
      item3Name,
      item3Company,
      item3Article,
      item4Text,
      item4Name,
      item4Company,
      item4Article,
      item5Text,
      item5Name,
      item5Company,
      item5Article,
    ],
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1));
    }, 20000);

    return () => clearInterval(intervalId);
  }, [testimonials.length]);

  const handlePrevTestimony = () => {
    setCurrentIndex(prevIndex => (prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1));
  };

  const handleNextTestimony = () => {
    setCurrentIndex(prevIndex => (prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1));
  };

  if (variant === 'cards') {
    const t = testimonials[currentIndex];
    return (
      <div className="home-testimony-slider">
        <button
          type="button"
          className="home-testimony-slider-btn home-testimony-slider-prev"
          onClick={handlePrevTestimony}
          aria-label={ariaPrev}
        >
          ‹
        </button>
        <div className="home-testimony-slider-track">
          <div
            key={currentIndex}
            className="home-testimony-card home-testimony-card-elevated home-testimony-slider-card"
          >
            <ChatBubbleLeftRightIcon className="home-testimony-icon" aria-hidden />
            <p className="home-testimony-text">{t.text}</p>
            <p className="home-testimony-author">
              <strong>{t.name}</strong> {t.company}
            </p>
            {t.article && (
              <a
                href={t.article}
                target="_blank"
                rel="noopener noreferrer"
                className="home-testimony-article"
              >
                {articleLink} <i className="bi-box-arrow-in-up-right" />
              </a>
            )}
          </div>
        </div>
        <button
          type="button"
          className="home-testimony-slider-btn home-testimony-slider-next"
          onClick={handleNextTestimony}
          aria-label={ariaNext}
        >
          ›
        </button>
        <div className="home-testimony-slider-dots" role="tablist" aria-label="Testimonials">
          {testimonials.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === currentIndex}
              aria-label={`Testimony ${i + 1}`}
              className={`home-testimony-slider-dot ${i === currentIndex ? 'active' : ''}`}
              onClick={() => setCurrentIndex(i)}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="testimony">
        <button className="carousel-control prev" onClick={handlePrevTestimony}>
          &lt;
        </button>
        <blockquote>
          <div>
            <div>
              <span className="quote-left">&ldquo;</span>
              <h6 className="px-4">{testimonials[currentIndex].text}</h6>
            </div>
            <span className="quote-right">&rdquo;</span>

            <div className="testimony-info">
              <p>
                <strong>{testimonials[currentIndex].name}</strong> {testimonials[currentIndex].company}
              </p>
              {testimonials[currentIndex].article && (
                <a href={testimonials[currentIndex].article} target="_blank" rel="noopener noreferrer">
                  {articleLink} <i className="bi-box-arrow-in-up-right"></i>
                </a>
              )}
            </div>
          </div>
        </blockquote>
        <button className="carousel-control next" onClick={handleNextTestimony}>
          &gt;
        </button>
      </div>
    </>
  );
}

export default Testimony;
